import { FormBlock } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, NumericFormat } from '@qbe/ads-core';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { useAppContent } from '~/hooks';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';

import type { SpecificBusinessDetailsForm } from '../SpecificBusinessDetails/types';

const Turnover = () => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme',
  });

  const { control } = useFormContext<SpecificBusinessDetailsForm>();
  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const getDefaultText = (value: number) => {
    const lessThan10000 = value && value < 10000;

    if (isPropertyOwner)
      return lessThan10000
        ? getTextContent('businessDetailsPropertyDetails.fields.rentalIncomeInput.helperText')
        : '';
    return lessThan10000
      ? getTextContent('businessDetailsSpecificBusinessDetails.fields.turnoverInput.helperText')
      : getTextContent(
          'businessDetailsSpecificBusinessDetails.fields.turnoverInput.roughEstimateHelperText'
        );
  };

  const errorMessage = isPropertyOwner ? 'Invalid rental income' : 'Invalid turnover';

  return (
    <>
      <FormGroupLabel
        title={
          isPropertyOwner
            ? getTextContent('businessDetailsPropertyDetails.fields.rentalIncomeInput.title')
            : getTextContent('businessDetailsSpecificBusinessDetails.fields.turnoverInput.title')
        }
        alignCenter
      />

      <FormBlock preset="input" maxWidth="100%">
        <Controller
          control={control}
          render={({ field: { ...field }, fieldState }) => {
            const isInvalid = Boolean(fieldState.error);

            let helperText: string = '';
            if (isInvalid) {
              helperText = errorMessage;
            } else {
              helperText = getDefaultText(field.value ?? 0);
            }

            return (
              <NumericFormat
                thousandSeparator
                id="turnover"
                name={field.name}
                label={
                  isPropertyOwner
                    ? getTextContent(
                        'businessDetailsPropertyDetails.fields.rentalIncomeInput.label'
                      )
                    : getTextContent(
                        'businessDetailsSpecificBusinessDetails.fields.turnoverInput.label'
                      )
                }
                required
                prefix="$ "
                allowNegative={false}
                onValueChange={(values) => field.onChange(values.floatValue ?? null)}
                value={field.value !== undefined && field.value !== null ? String(field.value) : ''}
                error={isInvalid}
                warning={!!field.value && field.value < 10000}
                displayIcon
                helperText={helperText}
                aria-labelledby="turnover"
                data-track={isPropertyOwner ? 'Enter annual rental income' : 'Enter turnover'}
                data-track-value="mask"
              />
            );
          }}
          name="turnover"
          rules={{
            min: {
              value: 1,
              message: errorMessage,
            },
            required: true,
          }}
        />
      </FormBlock>
    </>
  );
};

export default Turnover;
